import {
  APPOINTMENT_DEFAULT_DURATION,
  APPOINTMENT_REASON_LABELS,
  appointmentDefaultDuration,
  APPOINTMENT_REASONS,
  appointmentSlot,
  displayCadastralReference,
  displayName,
  counterVisitLabel,
  FOLLOW_UP_STEP_LABELS,
  INQUIRY_PURPOSE_LABELS,
  inspectionTitle,
  internalMeetingSpan,
  isPlannedOnSite,
  nextFollowUpStep,
  PASSAGE_NOTICE_REASON_LABELS,
  PASSAGE_NOTICE_REASONS,
  BUILDING_TYPE_LABELS,
  SEWER_NETWORK_TYPE_LABELS,
  townOf,
  type AppointmentReason,
  type AssignedInquiry,
  type PassageNoticeReason,
} from '@ghp/domain'
import {
  Badge,
  Button,
  DataField,
  DataValue,
  Field,
  InfoNote,
  Input,
  Modal,
  Select,
  Tag,
  Textarea,
  Wheel,
} from '@ghp/ui'
import {
  BellRing,
  CalendarClock,
  CalendarPlus,
  Check,
  ClipboardCheck,
  ClipboardList,
  Eye,
  Info,
  Navigation,
  StickyNote,
  TriangleAlert,
  WifiOff,
} from 'lucide-react'
import { Fragment, useEffect, useRef, useState } from 'react'

import type { OnSiteAppointment } from '../../../offline/outbox'
import { TOUR_STATE_PRESENTATION } from '../labels'
import {
  clampedDay,
  dayItems,
  durationItems,
  instantOf,
  MONTH_ITEMS,
  slotOf,
  timeItems,
  yearItems,
  type WheelSlot,
} from '../lib/appointment-wheels'
import { isHandedOver, type TourState } from '../lib/list-filters'
import { routeLinkOf } from '../lib/route-link'
import {
  tourFreeTimeFrom,
  tourSlotTaken,
  type TakenTourSlot,
  type TourOccupancy,
} from '../lib/tour-slot-taken'
import s from './OpeningSheet.module.css'

/**
 * La **pré-fiche d'ouverture** : ce que l'agent lit *avant* d'ouvrir la saisie d'un contrôle —
 * l'adresse, la commune, la référence, le rendez-vous et le contact à appeler.
 *
 * ⚠️ Le contact est celui **du rendez-vous** (`Inquiry.appointmentContactId`, M15), pas la
 * personne rencontrée : à cet instant, personne ne l'a encore été. Sur une vente, c'est
 * l'agence — et c'est elle qu'on appelle depuis le trottoir.
 *
 * ⚠️ **Plus aucun champ n'attend le modèle** : n° de parcelle, client puis réseau (M43)
 * descendent avec la tournée. Un « — » y dit que **le dossier se tait**, comme à la maquette
 * validée, qui ne l'accompagne d'aucune phrase. La note « pas encore descendus » de l'onglet
 * Infos (`NotYetSynced`) n'a donc plus sa place ici : elle ferait attendre à l'agent une donnée
 * que personne n'enverra.
 *
 * ⚠️ Rendez-vous et contact, eux, **disparaissent quand ils manquent** — la maquette les rend
 * conditionnels, et elle a raison : ce sont les deux seules lignes qui portent une conduite
 * (s'y rendre à l'heure, appeler avant), et un tiret cadratin y ferait chercher une donnée
 * absente au lieu de la faire oublier.
 */
export interface OpeningSheetProps {
  /**
   * Le contrôle à ouvrir, `null` quand la pré-fiche est fermée : c'est **lui** qui l'ouvre, la
   * fenêtre n'a pas d'état propre à tenir en plus.
   */
  inquiry: AssignedInquiry | null
  /**
   * L'état du contrôle **dans la tournée** (`tourStateOf`), qui décide de ce que la fenêtre
   * propose : ouvrir la saisie, ou la consulter quand le bureau tient déjà le contrôle.
   *
   * ⚠️ Pas `inquiry.status` — l'API cesse de servir la demande une fois déposée, son statut en
   * cache dit donc encore « Planifié » sur un contrôle transmis (#181). Absent, la fenêtre
   * ouvre : un appelant qui ne calcule pas cet état ne doit pas se retrouver à interdire la
   * saisie d'un contrôle qui reste à faire.
   */
  state?: TourState
  onClose: () => void
  /** Ouvrir la saisie — la navigation appartient à l'écran, pas à la fenêtre. */
  onOpen: (inquiry: AssignedInquiry) => void
  /**
   * Déposer un **avis de passage** : « nous sommes venus, personne n'a ouvert ».
   *
   * Prop de rappel, comme tout le reste de cette fenêtre : elle ignore la file locale, ce qui la
   * garde montable seule. Sans rappel, le geste **n'est pas offert** — un bouton mort au moment
   * où l'agent est devant une porte close serait pire que son absence.
   */
  /**
   * Une transmission de ce contrôle attend en file (`hasQueuedHandOver`) : la pré-fiche le traite
   * comme transmis — consultation seule, aucun avis de passage.
   */
  queued?: boolean
  /** `comment` : le commentaire libre, porté seulement par « Autre » (Sentry TERRAIN-6). */
  onPassageNotice?: (
    inquiry: AssignedInquiry,
    reason: PassageNoticeReason,
    comment?: string,
  ) => void
  /**
   * Les relances déposées sur cette demande et **encore en file** sur la tablette (M40) — ajoutées
   * à celles que le serveur a comptées (`remindersLeft`) pour numéroter la suivante.
   */
  pendingReminders?: number
  /**
   * Poser le **premier rendez-vous pris sur place** (#268) — sur un systématique ou une SYAM sans
   * rendez-vous, les seuls dont l'agent pose le premier créneau (`isPlannedOnSite`, #695). Il part par
   * l'outbox, hors réseau compris. Même règle que l'avis : sans rappel, le geste n'est pas offert.
   */
  onAppointment?: (inquiry: AssignedInquiry, appointment: OnSiteAppointment) => void
  /**
   * **Déplacer** un rendez-vous déjà pris, quel que soit le type (demande de GHP, 08/10/2026).
   * Sans lui, le geste n'est pas offert.
   */
  move?: AppointmentMove
  /**
   * La tournée et les points internes descendus (`useTourOccupancy`) : la fenêtre de rendez-vous
   * prévient quand le créneau en recouvre un (#669, Terrain 2.1c). Sans eux, rien n'est comparé.
   */
  tour?: TourOccupancy
}

/**
 * Le déplacement, **en ligne seulement** (D4) : il annule un créneau que l'usager a reçu par
 * e-mail, et un rejeu tardif écraserait ce que le bureau a posé entre-temps.
 */
export interface AppointmentMove {
  /** `null` quand le déplacement est possible ; sinon **la raison**, affichée sous le geste grisé. */
  unavailable: string | null
  /** Rejette avec le message à montrer mot pour mot quand le serveur refuse. */
  save: (inquiry: AssignedInquiry, move: AppointmentMoveRequest) => Promise<void>
}

export type AppointmentMoveRequest = OnSiteAppointment & {
  reason: AppointmentReason
  /**
   * La durée de la roue « Durée » (frame 2.1b, #578), **seulement si l'agent l'a changée** : sans
   * elle, le serveur garde celle du rendez-vous remplacé.
   */
  durationMinutes?: number
}

const REASON_OPTIONS = APPOINTMENT_REASONS.map((reason) => ({
  value: reason,
  label: APPOINTMENT_REASON_LABELS[reason],
}))

/**
 * Ce que le pied de fenêtre montre : les gestes, les motifs de l'avis, le commentaire d'un avis
 * « Autre » (`otherNotice`), la prise de rendez-vous, ou la confirmation d'un téléphone saisi sans
 * nom (`phoneWithoutName`).
 */
type SheetMode = 'actions' | 'notice' | 'otherNotice' | 'appointment' | 'phoneWithoutName'

/** La fenêtre « Planifier le rendez-vous » : le créneau aux roues, et la personne à contacter. */
interface AppointmentForm extends WheelSlot {
  name: string
  phone: string
  /** Le motif d'un déplacement (frame 2.1) — sans objet pour une première pose. */
  reason: AppointmentReason | null
  /** La durée d'un déplacement, en minutes (frame 2.1b) — sans objet pour une première pose. */
  duration: string
  comment: string
}

const NO_APPOINTMENT: AppointmentForm = {
  day: '',
  month: '',
  year: '',
  time: '',
  duration: String(APPOINTMENT_DEFAULT_DURATION),
  name: '',
  phone: '',
  reason: null,
  comment: '',
}

const MISSING = '—'

export function OpeningSheet({
  inquiry,
  state,
  queued,
  onClose,
  onOpen,
  onPassageNotice,
  pendingReminders = 0,
  onAppointment,
  move,
  tour,
}: OpeningSheetProps) {
  /**
   * Le motif se choisit **après** le bouton, dans la même fenêtre.
   *
   * ⚠️ **Le prototype n'en demande aucun**, et c'est un manque de sa part : sa propre liste
   * back-office affiche une colonne « Motif », qu'un geste à un seul appui ne peut pas remplir.
   * Et le motif n'est pas cosmétique — c'est lui qui oriente l'e-mail envoyé à l'usager.
   * Un appui, trois choix, aucun texte à saisir : c'est le minimum qui rende la colonne vraie.
   */
  const [mode, setMode] = useState<SheetMode>('actions')
  const [appointment, setAppointment] = useState(NO_APPOINTMENT)
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  // Un créneau pris se tranche au bouton, dans une fenêtre (#692, Terrain 2.1d).
  const [confirmingSlot, setConfirmingSlot] = useState(false)
  // Le commentaire d'un avis « Autre » (Sentry TERRAIN-6) : garage, logement vide…
  const [noticeComment, setNoticeComment] = useState('')
  const choosing = mode !== 'actions'
  // ⚠️ Le contenu se garde à la **fermeture**, état compris : la fenêtre s'efface en animation,
  // et repeindre une fiche vide pendant cette fraction de seconde donne l'impression d'un écran
  // qui a perdu ce qu'on venait d'ouvrir. L'état voyage avec la demande — séparés, le bouton
  // changerait de mot au milieu du fondu.
  const [shown, setShown] = useState<{
    inquiry: AssignedInquiry
    state?: TourState
    queued?: boolean
  } | null>(inquiry === null ? null : { inquiry, state, queued })

  // Chaque ouverture repart des actions par défaut : le composant reste monté entre deux
  // contrôles, et un choix de motif abandonné par la croix déposerait sinon l'avis du suivant.
  // Remis à zéro à l'**ouverture**, pas à la fermeture, pour la même raison que `shown`.
  useEffect(() => {
    if (inquiry === null) return
    setShown({ inquiry, state, queued })
    setMode('actions')
    setAppointment(NO_APPOINTMENT)
    setFailure(null)
    setConfirmingSlot(false)
    setNoticeComment('')
  }, [inquiry, state, queued])

  const contact = shown?.inquiry.contact ?? null
  // Ce que la fenêtre pré-remplit — et la référence pour savoir ce que l'agent a changé.
  const known = {
    name: contact === null ? '' : (displayName(contact) ?? ''),
    phone: contact?.phone ?? '',
    // La durée du rendez-vous en place — une heure pour une ligne d'avant #431, comme sa carte.
    duration: String(shown?.inquiry.appointmentDurationMinutes ?? APPOINTMENT_DEFAULT_DURATION),
  }
  const contactLine =
    contact === null
      ? ''
      : [displayName(contact), contact.phone].filter((part) => part !== null).join(' · ')
  // Le contrôle est parti au bureau : il n'y a plus rien à y saisir (D4). La règle est celle de
  // la vue « Transmis » de la liste, lue au même endroit qu'elle.
  const handedOver = shown?.state !== undefined && isHandedOver(shown.state)
  // Une transmission en file se lit comme une transmission : la fiche est verrouillée.
  const readOnly = handedOver || shown?.queued === true
  // L'avis, comme au prototype : ni sur un contrôle transmis (ou dont l'envoi attend), ni sur
  // un contrôle sans rendez-vous — un avis dirait à l'usager qu'on est passé le voir sans RDV.
  const offersNotice = onPassageNotice !== undefined && !readOnly && shown?.state !== 'unscheduled'
  // « À planifier » : l'agent planifie tout, sauf une vente ou une enquête hors secteur, que le
  // bureau planifie (décision de GHP du 04/11/2026).
  const unscheduled = !readOnly && shown?.state === 'unscheduled'
  const plannedOnSite =
    shown !== null && isPlannedOnSite(shown.inquiry.purpose ?? null, shown.inquiry.lot !== null)
  const offersAppointment = onAppointment !== undefined && unscheduled && plannedOnSite
  // La relance déposée (M40) : sur un contrôle sans rendez-vous qu'il planifie, l'agent la pose d'un appui,
  // et l'état de relance avance (n°1, n°2…).
  const offersReminder = onPassageNotice !== undefined && unscheduled && plannedOnSite
  const reminderNumber = (shown?.inquiry.remindersLeft ?? 0) + pendingReminders + 1
  // La suite du marché (M40), et l'étape où le bureau en est — la même règle que sa liste.
  // `?? []` : une ligne en cache d'avant le champ le relit `undefined`.
  const passageFollowUp = shown?.inquiry.passageFollowUp ?? []
  const currentFollowUpStep = nextFollowUpStep(
    passageFollowUp,
    (shown?.inquiry.passageFollowUpDone ?? []).map((step) => ({ step })),
  )
  const plannedByOffice = unscheduled && !plannedOnSite
  // ⚠️ **Déplacer, c'est remplacer** : le nouveau créneau annule l'ancien et part en confirmation
  // à l'usager (`PlanningService.scheduleFromField`), comme une pose du bureau.
  const rescheduling = (shown?.inquiry.scheduledAt ?? null) !== null
  const offersReschedule = move !== undefined && !readOnly && !unscheduled && rescheduling
  const scheduledAt = mode === 'appointment' ? instantOf(appointment) : null
  // ⚠️ **Une alerte, jamais un refus** (M21) : rien ne se grise à cause d'elle. Une première pose
  // prend la durée que le serveur posera, celle du type de contrôle (#578).
  const minutes = rescheduling
    ? Number(appointment.duration)
    : appointmentDefaultDuration(shown?.inquiry.purpose ?? null)
  const taken =
    scheduledAt === null || shown === null || tour === undefined
      ? null
      : tourSlotTaken(tour, scheduledAt, minutes, shown.inquiry.id)
  const freeTime =
    taken === null || scheduledAt === null || shown === null || tour === undefined
      ? null
      : tourFreeTimeFrom(tour, scheduledAt, minutes, shown.inquiry.id)
  // Un déplacement sans motif ne s'enregistre pas : c'est lui que le bureau lit dans son fil.
  const reasonMissing = rescheduling && appointment.reason === null
  // Un téléphone sans nom, sur un contrôle sans personne connue, devient un contact sans nom
  // (M24) : on le signale et on laisse rajouter un nom ; le téléphone part et s'affiche dans tous
  // les cas.
  const phoneWithoutName =
    optional(appointment.phone) !== null && optional(appointment.name) === null && contact === null
  const addedName = useRef<HTMLInputElement>(null)
  const commentField = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (mode === 'phoneWithoutName') addedName.current?.focus()
    if (mode === 'otherNotice') commentField.current?.focus()
  }, [mode])

  /** « Autre » n'enregistre rien d'un appui : il demande d'abord ce qu'il en est. */
  const startOtherNotice = () => {
    setNoticeComment('')
    setMode('otherNotice')
  }

  /** Après le créneau, le téléphone sans nom ; puis le rendez-vous part. */
  const confirm = () => {
    if (phoneWithoutName) setMode('phoneWithoutName')
    else void save()
  }

  /** Ouvre la fenêtre de planification, sur le créneau en place et le contact connu. */
  const startAppointment = () => {
    if (shown === null) return
    setAppointment({
      ...NO_APPOINTMENT,
      ...slotOf(shown.inquiry.scheduledAt ?? null),
      ...known,
    })
    setFailure(null)
    setMode('appointment')
  }

  /**
   * Pose le rendez-vous et ferme la fiche.
   *
   * ⚠️ **Seul ce que l'agent a modifié part** : le serveur crée une personne neuve pour un nom
   * (prénom et e-mail repartent vides) et corrige le téléphone de la personne affichée pour un
   * téléphone seul. Renvoyer le contact tel quel l'abîmerait ; un nom changé emporte le téléphone,
   * sans quoi la personne neuve n'en aurait aucun.
   *
   * Un déplacement attend la réponse du serveur, fenêtre ouverte : refusé, il reste à l'écran avec
   * le motif du refus, et rien n'a changé sur la tablette.
   */
  const save = async (withName = true) => {
    const at = instantOf(appointment)
    if (shown === null || at === null) return
    const name = withName ? optional(appointment.name) : null
    const phone = optional(appointment.phone)
    const nameChanged = name !== optional(known.name)
    const request: OnSiteAppointment = {
      scheduledAt: at,
      contactName: nameChanged ? name : null,
      contactPhone: nameChanged || phone !== optional(known.phone) ? phone : null,
      comment: optional(appointment.comment),
    }

    if (rescheduling) {
      if (move === undefined || appointment.reason === null) return
      setBusy(true)
      setFailure(null)
      try {
        await move.save(shown.inquiry, {
          ...request,
          reason: appointment.reason,
          // ⚠️ Touchée seulement, comme au bureau : la durée en cache n'est pas forcément celle
          // qui est posée, le bureau ayant pu la changer depuis la dernière descente.
          ...(appointment.duration === known.duration
            ? {}
            : { durationMinutes: Number(appointment.duration) }),
        })
      } catch (refusal: unknown) {
        setFailure(refusal instanceof Error ? refusal.message : String(refusal))
        setMode('appointment')
        return
      } finally {
        setBusy(false)
      }
    } else {
      onAppointment?.(shown.inquiry, request)
    }
    setMode('actions')
    onClose()
  }

  return (
    <Modal
      open={inquiry !== null}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      icon={mode === 'appointment' ? <CalendarClock size={18} /> : <ClipboardList size={18} />}
      title={
        mode === 'appointment'
          ? rescheduling
            ? 'Modifier le rendez-vous'
            : 'Planifier le rendez-vous'
          : inspectionTitle(shown?.inquiry ?? null)
      }
      subtitle={mode === 'appointment' && shown !== null ? subtitleOf(shown.inquiry) : undefined}
      busy={busy}
      error={failure}
      footer={
        mode === 'appointment' ? (
          // Le pied de la frame 2.4 : ouvrir la saisie, ou planifier. La croix abandonne.
          <>
            <Button
              tone="neutral"
              disabled={busy}
              onClick={() => {
                if (shown !== null) onOpen(shown.inquiry)
              }}
            >
              <ClipboardCheck size={18} aria-hidden /> Ouvrir le contrôle
            </Button>
            <Button
              tone="brand"
              disabled={busy || scheduledAt === null || reasonMissing}
              onClick={() => {
                if (taken !== null) setConfirmingSlot(true)
                else confirm()
              }}
            >
              <Check size={18} aria-hidden /> {rescheduling ? 'Enregistrer' : 'Planifier'}
            </Button>
          </>
        ) : (
          <div className={s.actions}>
            {/*
             * Le rendez-vous pris sur place (Terrain 0.9b, #268) et l'avis de passage partent tous deux
             * par l'outbox (`recordAppointment`, `recordPassageNotice`) : ce sont des **faits** que
             * seule la tablette connaît, et ils doivent partir hors réseau, devant la porte.
             */}
            {/* En tête et en geste principal (Sentry TERRAIN-5, demande de GHP du 29/10) : sur la
              tournée, ouvrir la fiche est le geste attendu ; l'avis et la relance suivent. Le
              prototype mettait l'avis d'abord — canvas à synchroniser. */}
            {!choosing && (
              <Button
                tone="brand"
                size="action"
                onClick={() => {
                  if (shown !== null) onOpen(shown.inquiry)
                }}
              >
                {readOnly ? (
                  <Eye size={18} aria-hidden />
                ) : (
                  <ClipboardCheck size={18} aria-hidden />
                )}
                {readOnly ? 'Consulter le contrôle' : 'Ouvrir le contrôle'}
                {readOnly && <span className={s.sub}>Lecture seule</span>}
              </Button>
            )}
            {/* Absent sur un contrôle déjà transmis : il n'y a plus de passage à constater. */}
            {offersNotice && !choosing && (
              <Button
                size="action"
                onClick={() => {
                  setMode('notice')
                }}
              >
                <BellRing size={18} aria-hidden /> Avis de passage
                <span className={s.sub}>Horodaté · notifie le back-office</span>
              </Button>
            )}
            {offersReminder && !choosing && (
              <Button
                size="action"
                onClick={() => {
                  if (shown !== null) onPassageNotice?.(shown.inquiry, 'reminder_left')
                  onClose()
                }}
              >
                <BellRing size={18} aria-hidden /> Relance n°{reminderNumber}
                <span className={s.sub}>Déposée dans la boîte · notifie le back-office</span>
              </Button>
            )}
            {/* Sans rendez-vous aussi, on peut refuser l'accès à l'agent (#574) : l'avis remonte au
                bureau comme les autres. « Absent au rendez-vous » reste réservé aux rendez-vous. */}
            {offersReminder && !choosing && (
              <Button
                size="action"
                onClick={() => {
                  if (shown !== null) onPassageNotice?.(shown.inquiry, 'access_refused')
                  onClose()
                }}
              >
                {PASSAGE_NOTICE_REASON_LABELS.access_refused}
                <span className={s.sub}>Horodaté · notifie le back-office</span>
              </Button>
            )}
            {/* Hors frame (Sentry TERRAIN-6, 29/10) : ni relance ni refus — un garage, un logement
                vide. Même rang que « Accès refusé », et le commentaire se demande avant l'envoi. */}
            {offersReminder && !choosing && (
              <Button size="action" onClick={startOtherNotice}>
                {PASSAGE_NOTICE_REASON_LABELS.other}
                <span className={s.sub}>Avec un commentaire · notifie le back-office</span>
              </Button>
            )}
            {/* Hors frame : la suite du marché, que seul le bureau voyait (#461). Rien ne part d'ici. */}
            {offersReminder && !choosing && passageFollowUp.length > 0 && (
              <InfoNote icon={<Info size={15} />}>
                Suite {shown?.inquiry.clientName} :{' '}
                {passageFollowUp.map((step, index) => (
                  <Fragment key={step}>
                    {index > 0 && ' → '}
                    {step === currentFollowUpStep ? (
                      <strong aria-current="step">{FOLLOW_UP_STEP_LABELS[step]}</strong>
                    ) : (
                      FOLLOW_UP_STEP_LABELS[step]
                    )}
                  </Fragment>
                ))}
              </InfoNote>
            )}
            {/* La note du bureau pour l'équipe (#433), écrite en planifiant. `?? null` : une ligne
                en cache d'avant le champ le relit `undefined`. */}
            {(shown?.inquiry.comment ?? null) !== null && (
              <InfoNote icon={<StickyNote size={15} />}>
                Note du bureau : {shown?.inquiry.comment}
              </InfoNote>
            )}
            {offersAppointment && !choosing && (
              <Button size="action" onClick={startAppointment}>
                <CalendarPlus size={18} aria-hidden /> Planifier le rendez-vous
                <span className={s.sub}>{onSiteSubtitle(shown.inquiry)}</span>
              </Button>
            )}
            {/* Frame 2.5. ⚠️ Sa mention « Ouvre l'agenda » n'est pas reprise : le geste ouvre la
              fenêtre de planification, et la mention mentirait. */}
            {offersReschedule && !choosing && (
              <Button
                tone="neutral"
                size="action"
                disabled={move.unavailable !== null}
                onClick={startAppointment}
              >
                <CalendarPlus size={18} aria-hidden /> Planifier un rendez-vous
                <span className={s.sub}>Remplace le créneau actuel</span>
              </Button>
            )}
            {offersReschedule && !choosing && move.unavailable !== null && (
              <InfoNote icon={<WifiOff size={15} />}>{move.unavailable}</InfoNote>
            )}
            {plannedByOffice && !choosing && shown !== null && (
              <InfoNote icon={<Info size={15} />}>{officeNote(shown.inquiry)}</InfoNote>
            )}
            {offersNotice &&
              mode === 'notice' &&
              // La relance déposée a son propre geste, sur un contrôle sans rendez-vous (M40).
              PASSAGE_NOTICE_REASONS.filter((reason) => reason !== 'reminder_left').map(
                (reason) => (
                  <Button
                    key={reason}
                    size="action"
                    onClick={() => {
                      if (reason === 'other') {
                        startOtherNotice()
                        return
                      }
                      if (shown !== null) onPassageNotice?.(shown.inquiry, reason)
                      setMode('actions')
                      onClose()
                    }}
                  >
                    {PASSAGE_NOTICE_REASON_LABELS[reason]}
                  </Button>
                ),
              )}
            {/* Hors frame (Sentry TERRAIN-6) : le commentaire est la seule chose que dit un avis
                « Autre » — rien ne part tant qu'il est vide. La croix ou « Annuler » renoncent. */}
            {mode === 'otherNotice' && (
              <>
                <Field label="Commentaire">
                  {({ id }) => (
                    <Textarea
                      ref={commentField}
                      id={id}
                      rows={3}
                      maxLength={500}
                      placeholder="Garage, logement vide…"
                      value={noticeComment}
                      onValueChange={setNoticeComment}
                    />
                  )}
                </Field>
                <Button
                  tone="brand"
                  size="action"
                  disabled={optional(noticeComment) === null}
                  onClick={() => {
                    const comment = optional(noticeComment)
                    if (shown === null || comment === null) return
                    onPassageNotice?.(shown.inquiry, 'other', comment)
                    setMode('actions')
                    onClose()
                  }}
                >
                  Enregistrer l’avis
                </Button>
              </>
            )}
            {/* Hors frame (décision du développeur, PR #370) : rajouter un nom ou enregistrer sans,
              le téléphone part dans les deux cas. La croix de la fenêtre abandonne. */}
            {mode === 'phoneWithoutName' && (
              <>
                <InfoNote tone="warn" icon={<TriangleAlert size={15} />}>
                  Aucun nom n’est saisi pour ce téléphone.
                </InfoNote>
                <Field label="Rajouter un nom">
                  {({ id }) => (
                    <Input
                      ref={addedName}
                      id={id}
                      value={appointment.name}
                      onValueChange={(name) => {
                        setAppointment((current) => ({ ...current, name }))
                      }}
                    />
                  )}
                </Field>
                <Button
                  tone="brand"
                  size="action"
                  disabled={busy || optional(appointment.name) === null}
                  onClick={() => {
                    void save()
                  }}
                >
                  Enregistrer avec ce nom
                </Button>
                <Button
                  tone="neutral"
                  size="action"
                  disabled={busy}
                  onClick={() => {
                    void save(false)
                  }}
                >
                  Enregistrer sans nom
                </Button>
              </>
            )}
            {/* Seulement pendant le choix du motif, pour **revenir au choix précédent** : se
              tromper de bouton ne doit pas obliger à rouvrir la fiche. Sinon la croix ferme,
              comme au prototype, qui n'a pas d'« Annuler » dans cette pile. */}
            {choosing && mode !== 'phoneWithoutName' && (
              <Button
                tone="ghost"
                size="action"
                onClick={() => {
                  setMode('actions')
                }}
              >
                Annuler
              </Button>
            )}
          </div>
        )
      }
    >
      {shown !== null && mode === 'appointment' && (
        <>
          <div className={s.rows}>
            <Field label="Nom de la personne" hint="Facultatif">
              {({ id, hintId }) => (
                <Input
                  id={id}
                  aria-describedby={hintId}
                  placeholder="Non communiqué"
                  value={appointment.name}
                  onValueChange={(name) => {
                    setAppointment((current) => ({ ...current, name }))
                  }}
                />
              )}
            </Field>
            <Field label="Téléphone" hint="Facultatif">
              {({ id, hintId }) => (
                <Input
                  id={id}
                  aria-describedby={hintId}
                  inputMode="tel"
                  placeholder="Non communiqué"
                  value={appointment.phone}
                  onValueChange={(phone) => {
                    setAppointment((current) => ({ ...current, phone }))
                  }}
                />
              )}
            </Field>
          </div>
          <DataField label="Date du rendez-vous" className={s.note}>
            <div className={s.wheels}>
              <Wheel
                label="Jour"
                items={dayItems(appointment.year, appointment.month)}
                value={clampedDay(appointment)}
                onValueChange={(day) => {
                  setAppointment((current) => ({ ...current, day }))
                }}
              />
              <Wheel
                label="Mois"
                items={MONTH_ITEMS}
                value={appointment.month}
                onValueChange={(month) => {
                  setAppointment((current) => ({ ...current, month }))
                }}
              />
              <Wheel
                label="Année"
                items={yearItems(appointment.year)}
                value={appointment.year}
                onValueChange={(year) => {
                  setAppointment((current) => ({ ...current, year }))
                }}
              />
            </div>
          </DataField>
          {/* La « plage horaire » des frames 2.1 et 2.2 revient en durée, pas en fin de plage
              (frame 2.1b, #578) : un rendez-vous porte un instant et une durée (#431). */}
          <div className={`${s.wheels} ${s.note}`}>
            <Wheel
              label="Heure · 07:00 → 17:00 · pas 30 min"
              items={timeItems(appointment.time)}
              value={appointment.time}
              onValueChange={(time) => {
                setAppointment((current) => ({ ...current, time }))
              }}
            />
            {rescheduling && (
              <Wheel
                label="Durée"
                items={durationItems(appointment.duration)}
                value={appointment.duration}
                onValueChange={(duration) => {
                  setAppointment((current) => ({ ...current, duration }))
                }}
              />
            )}
          </div>
          {rescheduling && (
            <Field label="Motif de la modification" className={s.note}>
              {({ labelId }) => (
                <Select
                  aria-labelledby={labelId}
                  placeholder="Sélectionner un motif…"
                  options={REASON_OPTIONS}
                  value={appointment.reason}
                  onValueChange={(reason) => {
                    setAppointment((current) => ({ ...current, reason }))
                  }}
                />
              )}
            </Field>
          )}
          <Field label="Commentaire" className={s.note}>
            {({ id }) => (
              <Textarea
                id={id}
                rows={3}
                placeholder="Précisez le contexte (facultatif)…"
                value={appointment.comment}
                onValueChange={(comment) => {
                  setAppointment((current) => ({ ...current, comment }))
                }}
              />
            )}
          </Field>
        </>
      )}
      {shown !== null && mode !== 'appointment' && (
        <>
          {/* Les pastilles du prototype, en tête du corps : l'état avant l'adresse, parce que
              c'est lui qui dit si la fenêtre ouvre une saisie ou une consultation. */}
          {shown.state !== undefined && (
            <p className={s.badges}>
              <Badge tone={TOUR_STATE_PRESENTATION[shown.state].tone}>
                {TOUR_STATE_PRESENTATION[shown.state].label}
              </Badge>
            </p>
          )}
          <div className={s.rows}>
            <DataField label="Adresse">
              <DataValue>
                <AddressRoute inquiry={shown.inquiry} />
              </DataValue>
            </DataField>
            <DataField label="Commune">
              <DataValue>{communeOf(shown.inquiry)}</DataValue>
            </DataField>
            <DataField label="Réf. GHP">
              <DataValue className={s.mono}>{shown.inquiry.reference ?? MISSING}</DataValue>
            </DataField>
            {/* La tournée porte la parcelle, le client et le réseau : l'onglet « Infos » les
                montre, la pré-fiche qui s'ouvre avant lui ne peut pas les dire absents. */}
            <DataField label="N° parcelle">
              <DataValue className={parcelOf(shown.inquiry) === null ? s.absent : s.mono}>
                {parcelOf(shown.inquiry) ?? MISSING}
              </DataValue>
            </DataField>
            <DataField label="Client">
              <DataValue className={shown.inquiry.clientName ? undefined : s.absent}>
                {shown.inquiry.clientName ?? MISSING}
              </DataValue>
            </DataField>
            {/* Le type de bâti du dossier (#586), comme l'onglet « Infos ». */}
            <DataField label="Type de bâti">
              <DataValue className={shown.inquiry.buildingType == null ? s.absent : undefined}>
                {shown.inquiry.buildingType == null
                  ? MISSING
                  : BUILDING_TYPE_LABELS[shown.inquiry.buildingType]}
              </DataValue>
            </DataField>
            <DataField label="Réseau">
              <DataValue className={networkOf(shown.inquiry) === null ? s.absent : undefined}>
                {networkOf(shown.inquiry) ?? MISSING}
              </DataValue>
            </DataField>
            {shown.inquiry.scheduledAt !== null && (
              <DataField label="Rendez-vous">
                <DataValue className={s.mono}>
                  {appointmentLabel(shown.inquiry.scheduledAt)}{' '}
                  {/* Le RDV1 ou le RDV2 qu'impose la suite d'un avis (#574) — `!= null` : une ligne
                      en cache d'avant le champ le relit `undefined`. */}
                  {shown.inquiry.appointmentImposedStep != null && (
                    <Tag size="sm" tone="blue">
                      {FOLLOW_UP_STEP_LABELS[shown.inquiry.appointmentImposedStep]}
                    </Tag>
                  )}
                </DataValue>
              </DataField>
            )}
            {contactLine !== '' && (
              <DataField label="Contact">
                {/* ⚠️ Ce qu'on sait du contact, jamais rien d'inventé : le nom seul quand le
                    numéro manque (un import client sans téléphone est courant, et c'est le seul
                    nom sous lequel se présenter), le numéro seul quand le nom manque (noté sur
                    place, #268). */}
                <DataValue>{contactLine}</DataValue>
              </DataField>
            )}
          </div>
        </>
      )}
      {confirmingSlot && taken !== null && scheduledAt !== null && shown !== null && (
        <SlotConflictDialog
          inquiry={shown.inquiry}
          scheduledAt={scheduledAt}
          durationMinutes={minutes}
          taken={taken}
          freeTime={freeTime}
          force={rescheduling ? 'Enregistrer quand même' : 'Planifier quand même'}
          onReplan={() => {
            if (freeTime !== null) setAppointment((current) => ({ ...current, time: freeTime }))
            setConfirmingSlot(false)
          }}
          onForce={() => {
            setConfirmingSlot(false)
            confirm()
          }}
          onClose={() => setConfirmingSlot(false)}
        />
      )}
    </Modal>
  )
}

/** « GHP-2026-0176 · 3 chemin de la Garenne · Carquefou » — le sous-titre de la frame 2.4. */
function subtitleOf(inquiry: AssignedInquiry): string {
  return [
    inquiry.reference ?? 'Sans référence',
    inquiry.address.street ?? 'Adresse inconnue',
    townOf(inquiry.address) ?? 'Commune inconnue',
  ].join(' · ')
}

/**
 * « Créneau déjà pris » (Terrain 2.1d, #692), la fenêtre du bureau (Back Office 1.9g) tournée à la
 * tablette : « tombe sur la tournée », et le pied au ton de la tablette. Elle s'ouvre **dans** la
 * fiche ; comparée à ce que la tablette a descendu, elle s'ouvre aussi hors ligne.
 *
 * « Replanifier » rend la fenêtre, l'heure changée — rien ne part tant qu'on n'a pas retouché.
 * « … quand même » pose tel quel : une alerte, jamais un refus (M21).
 */
function SlotConflictDialog({
  inquiry,
  scheduledAt,
  durationMinutes,
  taken,
  freeTime,
  force,
  onReplan,
  onForce,
  onClose,
}: {
  inquiry: AssignedInquiry
  scheduledAt: string
  durationMinutes: number
  taken: TakenTourSlot
  freeTime: string | null
  force: string
  onReplan: () => void
  onForce: () => void
  onClose: () => void
}) {
  const from = appointmentSlot(scheduledAt)
  const to = appointmentSlot(
    new Date(Date.parse(scheduledAt) + durationMinutes * 60_000).toISOString(),
  )

  return (
    <Modal
      open
      onOpenChange={() => onClose()}
      icon={<TriangleAlert size={18} />}
      title="Créneau déjà pris"
      footer={
        <>
          <Button tone="neutral" onClick={onForce}>
            {force}
          </Button>
          <Button tone="brand" onClick={onReplan}>
            <CalendarClock size={18} aria-hidden />{' '}
            {freeTime === null ? 'Replanifier' : `Replanifier à ${freeTime}`}
          </Button>
        </>
      }
    >
      <p>
        Le rendez-vous de <b>{inquiry.reference ?? 'Sans référence'}</b>
        {from === null || to === null ? '' : `, le ${from.day} de ${from.time} à ${to.time}`}, tombe
        sur la tournée :
      </p>
      <InfoNote tone="warn" icon={<TriangleAlert size={15} />}>
        {'inquiry' in taken ? 'Rendez-vous' : 'Point interne'} <TakenSubject taken={taken} />,{' '}
        {takenSpan(taken)}.
      </InfoNote>
      <p>
        {freeTime === null
          ? 'Plus aucune heure ne se libère ce jour-là : « Replanifier » vous rend la fenêtre.'
          : `${freeTime} est la première heure libre de la tournée après ce créneau.`}
      </p>
    </Modal>
  )
}

/** « de 09:30 à 10:30 » : la plage de ce qui occupe le créneau ; un séminaire, comme sa carte (#670). */
function takenSpan(taken: TakenTourSlot): string {
  if (
    'meeting' in taken &&
    internalMeetingSpan(taken.scheduledAt, taken.durationMinutes)?.wholeDays === true
  ) {
    return 'journée entière'
  }

  const from = appointmentSlot(taken.scheduledAt)
  const to = appointmentSlot(
    new Date(Date.parse(taken.scheduledAt) + taken.durationMinutes * 60_000).toISOString(),
  )

  return from === null || to === null ? '' : `de ${from.time} à ${to.time}`
}

/** « GHP-2026-0223, 4 rue du Port (Thouaré-sur-Loire) » ou « Point sécurité, Dépôt Ste-Luce ». */
function TakenSubject({ taken }: { taken: TakenTourSlot }) {
  if ('meeting' in taken) {
    return (
      <>
        <b>{taken.meeting.title}</b>
        {taken.meeting.place === null ? '' : `, ${taken.meeting.place}`}
      </>
    )
  }

  const { reference, address } = taken.inquiry
  const town = townOf(address)

  return (
    <>
      <b>{reference ?? 'Sans référence'}</b>, {address.street ?? 'adresse non renseignée'}
      {town === null ? '' : ` (${town})`}
    </>
  )
}

/** Un champ laissé blanc vaut « Non communiqué ». */
function optional(text: string): string | null {
  const trimmed = text.trim()

  return trimmed === '' ? null : trimmed
}

/** La phrase du prototype pour un contrôle que le bureau planifie (frame Terrain 2.6). */
/**
 * Le sous-titre de « Planifier le rendez-vous » : « Contrôle systématique » de la frame 2.3, et pour
 * les autres types que l'agent planifie sur place (décision de GHP du 04/11/2026), leur libellé.
 */
function onSiteSubtitle(inquiry: AssignedInquiry): string {
  if (inquiry.purpose === 'systematic') return 'Contrôle systématique'
  const label = (INQUIRY_PURPOSE_LABELS as Record<string, string | undefined>)[
    inquiry.purpose ?? ''
  ]

  return label === undefined ? 'Contrôle' : `Contrôle ${label}`
}

function officeNote(inquiry: AssignedInquiry): string {
  const label = counterVisitLabel(inquiry.purpose ?? null, inquiry.followUpRank ?? null)

  return label === null
    ? 'Ce contrôle est planifié par le back-office.'
    : `Ce contrôle « ${label} » est planifié par le back-office.`
}

/**
 * L'adresse, **et l'itinéraire d'un appui** (#319) : c'est ici que l'agent la lit avant de
 * partir, donc ici qu'il doit pouvoir la confier à son application de cartes (`routeLinkOf`).
 *
 * ⚠️ « Itinéraire » est **écrit**, pas seulement dessiné : au doigt rien ne se survole, et une
 * adresse simplement colorée ne dit pas qu'elle s'ouvre — ni ce qu'elle ouvre. Le mot fait partie
 * du nom du lien, ce que l'icône seule ne ferait pas.
 */
function AddressRoute({ inquiry }: { inquiry: AssignedInquiry }) {
  const street = inquiry.address.street ?? MISSING
  const route = routeLinkOf(inquiry.address)

  if (route === null) return street

  return (
    <a className={s.route} href={route}>
      {street}
      <span className={s.routeHint}>
        <Navigation size={14} aria-hidden />
        Itinéraire
      </span>
    </a>
  )
}

/** « Nantes · 44000 », l'ordre de la maquette — la commune d'abord, le code postal ensuite. */
function communeOf(inquiry: AssignedInquiry): string {
  const town = townOf(inquiry.address)
  const { postalCode } = inquiry.address

  if (town === null && postalCode === null) return MISSING

  return [town, postalCode].filter((part) => part !== null).join(' · ')
}

/**
 * « mardi 4 août 2026 à 08:30 » — le jour **nommé**, parce que la pré-fiche s'ouvre justement
 * quand l'agent se demande si c'est bien aujourd'hui.
 *
 * Une date illisible se rend telle quelle plutôt que de vider la ligne : la fiche reste lisible,
 * et l'agent voit ce que la tablette a reçu (M21).
 */
function appointmentLabel(scheduledAt: string): string {
  const slot = appointmentSlot(scheduledAt)

  return slot === null ? scheduledAt : `${slot.namedDay} à ${slot.time}`
}

/**
 * La parcelle **telle que le rapport l'imprime** (`displayCadastralReference`) : normalisée
 * (« AB0100 »), et tirée de la réf. GHP quand le dossier n'en porte pas — la tablette affichait
 * « — » ou « AB 100 » pour une fiche dont le PDF dit « AB0100 ».
 */
function parcelOf(inquiry: AssignedInquiry): string | null {
  return displayCadastralReference({
    cadastralReference: inquiry.cadastralReference ?? null,
    reference: inquiry.reference,
  })
}

/**
 * Le réseau **du dossier** (M43) — pas celui que l'agent constatera sur place. `?? null` : une
 * tournée en cache d'avant le champ le relit `undefined` (D22).
 */
function networkOf(inquiry: AssignedInquiry): string | null {
  const network = inquiry.sewerNetworkType ?? null

  return network === null ? null : SEWER_NETWORK_TYPE_LABELS[network]
}
